import React, { useState, useEffect } from "react";
import Pagination from "@mui/material/Pagination";
import { ColorRing } from "react-loader-spinner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

import API_BASE_URL from "../../../../Constants";
import ListHeader from "../../../../components/ListHeader/NoCardView/index";
import ViewInspectionDeviceModal from "./ViewInspectionDeviceModal";
import InspectionWizardModal from "../../../setup/components/Building/Actions/Run the Inspection Wizard/InspectionWizardModalNoDropdown";

// Replace with the columns you want:
const ProcessViewColumns = [
  { key: "actions", label: "Actions" },
  { key: "inspectionNumber", label: "Inspection #" },
  { key: "agencyName", label: "Agency Name" },
  { key: "test", label: "Inspection Test" },
  //   { key: "managementCompanyName", label: "Mgmt Co" },
  { key: "buildingReference", label: "Building Ref" },
  { key: "buildingSite", label: "Building Site" },
  { key: "status", label: "Status" },
  { key: "performingCompanyName", label: "Performing Co" },
  { key: "witnessCompanyName", label: "Witness Co" },
  { key: "lastDate", label: "Last Date" },
  // Add more if you want, e.g. approvalDate, nextDate, inspYear...
];

const QuickViewColumns = [
  { key: "actions", label: "Actions" },
  { key: "inspectionNumber", label: "Inspection #" },
  { key: "agencyName", label: "Agency" },
  { key: "test", label: "Test Label" },
  //   { key: "managementCompanyName", label: "Mgmt Co" },
  { key: "buildingSite", label: "Building Site" },
  { key: "status", label: "Status" },
  { key: "performingCompanyName", label: "Performing Co" },
  { key: "witnessCompanyName", label: "Witness Co" },
  { key: "lastDate", label: "Last Date" },
];

export default function InspectionsList() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState("");
  const [inspections, setInspections] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInspectionId, setSelectedInspectionId] = useState(null);
  const [selectedInspectionNumber, setSelectedInspectionNumber] =
    useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // For service company name lookup (if needed)
  const [serviceCompanies, setServiceCompanies] = useState([]);

  // Which view? "quickView" or "processView"
  const [selectedView, setSelectedView] = useState("quickView");

  // 1) fetch agencies on mount
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencies`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch agencies. Status: ${res.status}`);
        }
        const data = await res.json();
        setAgencies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching agencies:", err);
        setAgencies([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // 2) fetch tests whenever selectedAgencyId changes
  useEffect(() => {
    if (!selectedAgencyId) {
      setTests([]);
      setSelectedTestId("");
      return;
    }
    (async () => {
      try {
        setIsLoading(true);
        const url = `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${selectedAgencyId}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          throw new Error(`Failed to fetch tests. Status: ${res.status}`);
        }
        const data = await res.json();
        setTests(Array.isArray(data) ? data : []);
        setSelectedTestId("");
      } catch (err) {
        console.error("Error fetching tests:", err);
        setTests([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedAgencyId]);

  // 3) fetch inspections whenever filters / pagination change
  useEffect(() => {
    fetchInspections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgencyId, selectedTestId, currentPage, rowsPerPage]);

  const fetchInspections = async () => {
    try {
      setIsLoading(true);
      const validPage = isNaN(currentPage) || currentPage < 1 ? 1 : currentPage;
      const validSize =
        isNaN(rowsPerPage) || rowsPerPage < 1 ? 10 : rowsPerPage;

      const params = new URLSearchParams();
      params.append("page", validPage - 1);
      params.append("size", validSize);
      if (searchQuery.trim()) {
        params.append("q", searchQuery.trim());
      }
      if (selectedAgencyId) {
        params.append("agencyId", selectedAgencyId);
      }
      if (selectedTestId) {
        params.append("testId", selectedTestId);
      }

      const url = `${API_BASE_URL}/api/inspections/with-joins-listdto?${params.toString()}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`Fetch error => status ${response.status}`);
      }

      const data = await response.json(); // should be Page<InspectionsListDto2>
      setInspections(Array.isArray(data.content) ? data.content : []);

      setCurrentPage(data.number + 1);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error("Error fetching inspections:", error);
      setInspections([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  // 4) fetch all service companies if you need them
  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/inspections/service-companies`,
          {
            credentials: "include",
          }
        );
        if (!response.ok) throw new Error("Failed fetching companies");
        const data = await response.json();
        setServiceCompanies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setServiceCompanies([]);
      }
    })();
  }, []);

  // Handlers
  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleSearch = () => {
    setCurrentPage(1);
    fetchInspections();
  };

  const exportData = (dataToExport, filename) => {
    if (!dataToExport?.length) {
      alert("No data to export!");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  const handleViewClick = (inspId, inspNumber) => {
    const found = inspections.find((i) => i.inspectionId === inspId);
    if (found) {
      setSelectedInspection(found);
      setSelectedInspectionId(inspId);
      setSelectedInspectionNumber(inspNumber);
      setIsModalOpen(true);
    }
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInspection(null);
    setSelectedInspectionNumber(null);
    setSelectedInspectionId(null);
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
  };
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleOpenCreateModal = () => setIsWizardOpen(true);
  const handleCloseWizard = () => setIsWizardOpen(false);

  const handlePrev = () => {
    // switch to previous inspection
    const currentIndex = inspections.findIndex(
      (i) => i.inspectionId === selectedInspectionId
    );
    if (currentIndex > 0) {
      const prevInspection = inspections[currentIndex - 1];
      setSelectedInspection(prevInspection);
      setSelectedInspectionId(prevInspection.inspectionId);
      setSelectedInspectionNumber(prevInspection.inspectionNumber);
    }
  };

  const handleNext = () => {
    // switch to next inspection
    const currentIndex = inspections.findIndex(
      (i) => i.inspectionId === selectedInspectionId
    );
    if (currentIndex < inspections.length - 1) {
      const nextInspection = inspections[currentIndex + 1];
      setSelectedInspection(nextInspection);
      setSelectedInspectionId(nextInspection.inspectionId);
      setSelectedInspectionNumber(nextInspection.inspectionNumber);
    }
  };

  return (
    <div className="w-full h-screen flex flex-col">
      <ListHeader
        title="Inspections List"
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearch={handleSearch}
        handleOpenCreateModal={handleOpenCreateModal}
      >
        {/* Add Map View Button */}
        <button
          onClick={() =>
            navigate("/inspections-violations/inspections-list-map-view")
          }
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg text-sss-blue-sel font-bold hover:bg-sss-blue-sel hover:text-white transition mr-2"
        >
          Map View
        </button>

        {/* Add Dropdown for View Selection */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-semibold">View:</label>
          <select
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value)}
            className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                       hover:text-white transition"
          >
            <option value="quickView">Quick View List</option>
            <option value="processView">Inspection Process List</option>
          </select>
        </div>

        <div className="flex items-center space-x-2 whitespace-nowrap">
          <label className="text-sm font-semibold">Agency</label>
          <select
            value={selectedAgencyId}
            onChange={(e) => setSelectedAgencyId(e.target.value)}
            className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                       hover:text-white transition"
          >
            <option value="">All</option>
            {agencies.map((ag) => (
              <option key={ag.agencyId} value={ag.agencyId}>
                {ag.agencyName || `Agency #${ag.agencyId}`}
              </option>
            ))}
          </select>

          <label className="text-sm font-semibold">Test</label>
          <select
            value={selectedTestId}
            onChange={(e) => setSelectedTestId(e.target.value)}
            disabled={!selectedAgencyId}
            className={`px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                       hover:text-white transition ${
                         !selectedAgencyId ? "cursor-not-allowed" : ""
                       }`}
          >
            <option value="">All</option>
            {tests.map((t) => (
              <option key={t.testId} value={t.testId}>
                {t.label ?? `Test #${t.testId}`}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => exportData(inspections, "AllInspectionsList.xlsx")}
            className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                       hover:text-white transition"
          >
            Excel
          </button>
        </div>
      </ListHeader>

      <hr className="border-gray-200 mb-2" />

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <ColorRing
              visible
              height="200"
              width="200"
              ariaLabel="color-ring-loading"
              colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
            />
          </div>
        ) : (
          <div
            className={selectedView === "processView" ? "overflow-x-auto" : ""}
          >
            <table
              className={`min-w-full bg-white border-2 border-gray-200 ${
                selectedView === "processView" ? "min-w-max" : ""
              }`}
            >
              <thead className="bg-gray-200">
                <tr>
                  {(selectedView === "quickView"
                    ? QuickViewColumns
                    : ProcessViewColumns
                  ).map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-2 text-left font-bold text-sss-blue-sel whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {inspections.map((insp, idx) => (
                  <tr
                    key={idx}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    {(selectedView === "quickView"
                      ? QuickViewColumns
                      : ProcessViewColumns
                    ).map((col) => {
                      // 1) The "View" button
                      if (col.key === "actions") {
                        return (
                          <td
                            key={col.key}
                            className="px-4 py-2 whitespace-nowrap"
                          >
                            <button
                              className="text-sss-info font-semibold hover:underline"
                              onClick={() =>
                                handleViewClick(
                                  insp.inspectionId,
                                  insp.inspectionNumber
                                )
                              }
                            >
                              View
                            </button>
                          </td>
                        );
                      }

                      // 2) buildingReference vs buildingSite logic
                      if (col.key === "buildingReference") {
                        return (
                          <td
                            key={col.key}
                            className="px-4 py-2 whitespace-nowrap"
                          >
                            {insp.buildingReference || "N/A"}
                          </td>
                        );
                      }
                      if (col.key === "buildingSite") {
                        return (
                          <td
                            key={col.key}
                            className="px-4 py-2 whitespace-nowrap"
                          >
                            {insp.buildingSite ||
                              insp.buildingReference ||
                              "N/A"}
                          </td>
                        );
                      }

                      // 3) For lastDate (or other date fields), display date+time
                      if (col.key === "lastDate") {
                        const rawValue = insp.lastDate;
                        return (
                          <td
                            key={col.key}
                            className="px-4 py-2 whitespace-nowrap"
                          >
                            {rawValue
                              ? new Date(rawValue).toLocaleString()
                              : "N/A"}
                          </td>
                        );
                      }

                      // 4) Default: just display insp[col.key]
                      return (
                        <td
                          key={col.key}
                          className="px-4 py-2 whitespace-nowrap"
                        >
                          {insp[col.key] ?? "N/A"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!inspections.length && (
                  <tr>
                    <td
                      colSpan={
                        selectedView === "quickView"
                          ? QuickViewColumns.length
                          : ProcessViewColumns.length
                      }
                      className="text-center py-4"
                    >
                      No inspections found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination / footer */}
      <div
        className="fixed bottom-0 left-0 w-full bg-white border-t
                   border-gray-300 p-4 flex justify-between items-center"
      >
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-gray-700">Rows per page:</span>
          <select
            className="border border-gray-300 rounded px-2 py-1 focus:outline-none"
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {/* The "View Device Modal" */}
      {isModalOpen && selectedInspectionId && (
        <ViewInspectionDeviceModal
          open={isModalOpen}
          onClose={handleCloseModal}
          refreshList={fetchInspections}
          numericInspectionId={selectedInspectionId}
          inspectionNumber={selectedInspectionNumber}
          selectedInspection={selectedInspection}
          setNext={handleNext}
          setPrev={handlePrev}
        />
      )}

      {/* "Create Inspection" wizard */}
      {isWizardOpen && (
        <InspectionWizardModal
          onClose={handleCloseWizard}
          refreshList={fetchInspections}
        />
      )}
    </div>
  );
}
